import {
  Button,
  Checkbox,
  TextField,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  FormControlLabel,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material'
import { DateTimePicker } from '@mui/x-date-pickers'
import axios from 'axios'
import PageAppBar from 'components/PageAppBar'
import dayjs from 'dayjs'
import { base_url } from 'env'
import { useState } from 'react'
import { useLoaderData, useNavigate } from 'react-router'
import { UserModel } from 'types'
import { toast } from 'react-hot-toast'

const accountStates = [
  'active',
  'inactive',
  'banned',
  'deleted',
  'frozen',
  'normal',
]

export default function UserEditPage() {
  const navigate = useNavigate()
  const initialData = useLoaderData() as UserModel
  const oldLogin = initialData.login

  const [user, setUser] = useState<UserModel>(initialData)
  const [isSaving, setIsSaving] = useState(false)

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setUser((prev) => ({ ...prev, [name]: value }))
  }

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target
    setUser((prev) => ({ ...prev, [name]: checked }))
  }

  const handleSelectChange = (e: any) => {
    const { name, value } = e.target
    setUser((prev) => ({ ...prev, [name]: value }))
  }

  const handleSubmit = () => {
    setIsSaving(true)

    const payload = {
      admin: user.admin,
      login: user.login,
      email: user.email,
      firstName: user.firstName || null,
      lastName: user.lastName || null,
      phone: user.phone || null,
      accountState: user.accountState,
    }

    axios
      .put(`${base_url}/user/${oldLogin}`, payload)
      .then((response) => {
        toast.success('Utilisateur mis à jour avec succès !')
        navigate(`/user/${response.data.login}`)
      })
      .catch((error) => {
        console.error('Failed to update user:', error)
      })
      .finally(() => {
        setIsSaving(false)
      })
  }

  return (
    <PageAppBar pageName={`Modifier l'utilisateur: ${oldLogin}`}>
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <Card sx={{ maxWidth: '800px', width: '100%' }}>
          <CardContent>
            <Typography variant="h5" sx={{ mb: 2 }}>
              Détails du compte
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  name="login"
                  label="Identifiant"
                  value={user.login}
                  onChange={handleChange}
                  disabled={isSaving}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  name="firstName"
                  label="Prénom"
                  value={user.firstName || ''}
                  onChange={handleChange}
                  disabled={isSaving}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  name="lastName"
                  label="Nom"
                  value={user.lastName || ''}
                  onChange={handleChange}
                  disabled={isSaving}
                />
              </Grid>
              <Grid item xs={12} sm={8}>
                <TextField
                  fullWidth
                  name="email"
                  type="email"
                  label="Email"
                  value={user.email}
                  onChange={handleChange}
                  disabled={isSaving}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  name="phone"
                  type="number"
                  label="Téléphone"
                  value={user.phone || ''}
                  onChange={handleChange}
                  disabled={isSaving}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth disabled={isSaving}>
                  <InputLabel id="account-state-label">
                    État du compte
                  </InputLabel>
                  <Select
                    labelId="account-state-label"
                    name="accountState"
                    value={user.accountState}
                    label="État du compte"
                    onChange={handleSelectChange}
                  >
                    {accountStates.map((state) => (
                      <MenuItem key={state} value={state}>
                        {state}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid
                item
                xs={12}
                sm={6}
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      name="admin"
                      checked={user.admin}
                      onChange={handleCheckboxChange}
                      disabled={isSaving}
                    />
                  }
                  label="Administrateur"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Clé de licence assignée"
                  value={user.license_key || 'Aucune'}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DateTimePicker
                  label="Date de dernière connexion"
                  value={dayjs(
                    user.lastConnectionDate
                      ? user.lastConnectionDate * 1000
                      : null
                  )}
                  readOnly
                  sx={{ width: '100%' }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DateTimePicker
                  label="Date de création"
                  value={dayjs(Number(user.creationDate) * 1000)}
                  readOnly
                  sx={{ width: '100%' }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DateTimePicker
                  label="Date de suppression prévue"
                  value={dayjs(user.flagDate ? user.flagDate * 1000 : null)}
                  readOnly
                  sx={{ width: '100%' }}
                />
              </Grid>
            </Grid>
          </CardContent>
          <CardActions sx={{ p: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              color="success"
              onClick={handleSubmit}
              disabled={isSaving}
            >
              {isSaving ? <CircularProgress size={24} /> : 'Sauvegarder'}
            </Button>
          </CardActions>
        </Card>
      </Box>
    </PageAppBar>
  )
}
